/**
 * The public tour pages' words, Hebrew first, English beside it. The privacy
 * notice is versioned on the server (TOUR_CONSENT_VERSION in
 * packages/api/src/modules/housing/tour-booking.service.ts): change the
 * notice text here and bump that version together.
 */
export type TourLang = 'he' | 'en';

export interface TourStrings {
  dir: 'rtl' | 'ltr';
  locale: string;
  otherLang: { lang: TourLang; label: string };
  title: string;
  subtitle: string;
  unitType: string;
  unitTypeAny: string;
  chooseDay: string;
  chooseTime: string;
  full: string;
  taken: string;
  noSlotsTitle: string;
  noSlotsBody: string;
  yourDetails: string;
  name: string;
  email: string;
  emailHint: string;
  phone: string;
  optional: string;
  notice: (operator: string, pendingHours: number) => string;
  noticeLegal: string;
  privacyPolicy: (operator: string) => string;
  marketing: (operator: string) => string;
  submit: string;
  sending: string;
  sentTitle: string;
  sentBody: (email: string, pendingHours: number) => string;
  sentSpam: string;
  notFoundTitle: string;
  notFoundBody: string;
  loadError: string;
  retry: string;
  errors: Record<string, string>;
  required: string;
  badEmail: string;
  badPhone: string;
  changeTime: string;
  // The manage link
  manageTitle: string;
  pendingBody: string;
  confirm: string;
  confirming: string;
  confirmedTitle: string;
  confirmedBody: string;
  cancel: string;
  cancelAsk: string;
  cancelYes: string;
  cancelNo: string;
  cancelledTitle: string;
  cancelledBody: string;
  expiredTitle: string;
  expiredBody: string;
  pastTitle: string;
  pastBody: string;
  bookAgain: string;
  linkInvalidTitle: string;
  linkInvalidBody: string;
  when: string;
  what: string;
}

export const TOUR_STRINGS: Record<TourLang, TourStrings> = {
  he: {
    dir: 'rtl',
    locale: 'he-IL',
    otherLang: { lang: 'en', label: 'English' },
    title: 'קביעת סיור',
    subtitle: 'בחרו מועד, ונשלח לכם מייל לאישור הסיור.',
    unitType: 'איזו דירה מעניינת אתכם?',
    unitTypeAny: 'עוד לא יודע/ת',
    chooseDay: 'באיזה יום?',
    chooseTime: 'באיזו שעה?',
    full: 'מלא',
    taken: 'תפוס',
    noSlotsTitle: 'אין כרגע מועדים פנויים',
    noSlotsBody: 'כל המועדים בשבועיים הקרובים תפוסים. נסו שוב בעוד כמה ימים.',
    yourDetails: 'הפרטים שלכם',
    name: 'שם מלא',
    email: 'מייל',
    emailHint: 'לשם נשלח את קישור האישור',
    phone: 'טלפון',
    optional: 'לא חובה',
    notice: (operator, hours) =>
      `${operator} אוסף/ת את הפרטים כדי לתאם איתכם את הסיור. ${operator} הוא בעל המאגר והאחראי לפרטים, ו-TendedLoop מעבדת אותם עבורו. הטלפון וסוג הדירה אינם חובה. בלי אישור בקישור שבמייל תוך ${hours} שעות הבקשה נמחקת, ואם לא תתקדמו לחוזה הפרטים יימחקו אחרי 90 יום. נשלח לכם רק הודעות על הסיור הזה.`,
    noticeLegal: 'אין חובה חוקית למסור את הפרטים, אבל בלי שם ומייל לא נוכל לקבוע את הסיור.',
    privacyPolicy: (operator) => `מדיניות הפרטיות של ${operator}`,
    marketing: (operator) => `אשמח לקבל מ${operator} עדכונים על דירות פנויות במייל (לא חובה)`,
    submit: 'קביעת הסיור',
    sending: 'שולחים…',
    sentTitle: 'בדקו את המייל',
    sentBody: (email, hours) => `שלחנו קישור לאישור הסיור אל ${email}. המועד שמור לכם ${hours} שעות, עד שתאשרו.`,
    sentSpam: 'לא הגיע? בדקו בתיקיית הספאם או בקידומי מכירות.',
    notFoundTitle: 'הדף אינו זמין',
    notFoundBody: 'אין כאן דף לקביעת סיור. בדקו את הקישור או פנו למשרד.',
    loadError: 'לא הצלחנו לטעון את הדף. בדקו את החיבור ונסו שוב.',
    retry: 'נסו שוב',
    errors: {
      TOUR_SLOT_TAKEN: 'המועד הזה נתפס הרגע. בחרו מועד אחר.',
      TOUR_SLOT_UNAVAILABLE: 'המועד הזה כבר לא זמין. בחרו מועד אחר.',
      TOUR_NOTICE_CHANGED: 'הודעת הפרטיות עודכנה. קראו אותה שוב ושלחו.',
      TOUR_BOT_CHECK: 'בדיקת האבטחה לא עברה. נסו שוב.',
      RATE_LIMITED: 'יותר מדי ניסיונות. נסו שוב מאוחר יותר.',
      TOUR_EXPIRED: 'הבקשה פגה. אפשר לקבוע סיור חדש.',
      TOUR_NOT_CANCELLABLE: 'אי אפשר לבטל את הסיור הזה דרך הקישור. פנו למשרד.',
      DEFAULT: 'משהו השתבש. נסו שוב.',
    },
    required: 'שדה חובה',
    badEmail: 'כתובת המייל לא נראית תקינה',
    badPhone: 'מספרים בלבד',
    changeTime: 'שינוי מועד',
    manageTitle: 'הסיור שלכם',
    pendingBody: 'הסיור עוד לא אושר. לחצו כדי לשמור את המועד.',
    confirm: 'אישור הסיור',
    confirming: 'מאשרים…',
    confirmedTitle: 'הסיור נקבע',
    confirmedBody: 'נשלח אליכם מייל עם הפרטים. נשמח לראותכם.',
    cancel: 'ביטול הסיור',
    cancelAsk: 'לבטל את הסיור?',
    cancelYes: 'כן, לבטל',
    cancelNo: 'לא, להשאיר',
    cancelledTitle: 'הסיור בוטל',
    cancelledBody: 'המועד התפנה. אפשר לקבוע מועד חדש בכל עת.',
    expiredTitle: 'הבקשה פגה',
    expiredBody: 'הסיור לא אושר בזמן ולכן לא נשמר.',
    pastTitle: 'הסיור כבר עבר',
    pastBody: 'לסיור נוסף, קבעו מועד חדש.',
    bookAgain: 'קביעת סיור חדש',
    linkInvalidTitle: 'הקישור אינו תקף',
    linkInvalidBody: 'ייתכן שהקישור חלקי או ישן. פתחו אותו שוב מהמייל, או פנו למשרד.',
    when: 'מתי',
    what: 'סוג דירה',
  },
  en: {
    dir: 'ltr',
    locale: 'en-GB',
    otherLang: { lang: 'he', label: 'עברית' },
    title: 'Book a tour',
    subtitle: 'Pick a time and we will email you a link to confirm it.',
    unitType: 'Which home interests you?',
    unitTypeAny: 'Not sure yet',
    chooseDay: 'Which day?',
    chooseTime: 'What time?',
    full: 'Full',
    taken: 'Taken',
    noSlotsTitle: 'No times available right now',
    noSlotsBody: 'Every time in the next two weeks is taken. Please try again in a few days.',
    yourDetails: 'Your details',
    name: 'Full name',
    email: 'Email',
    emailHint: 'We send the confirmation link here',
    phone: 'Phone',
    optional: 'optional',
    notice: (operator, hours) =>
      `${operator} collects these details to arrange your tour. ${operator} is the controller of this data and TendedLoop processes it on their behalf. Phone and home type are optional. Without a confirmation from the emailed link within ${hours} hours the request is deleted, and if you do not go on to a lease your details are deleted after 90 days. We only send you messages about this tour.`,
    noticeLegal:
      'You are under no legal duty to give these details, but without a name and email we cannot book the tour.',
    privacyPolicy: (operator) => `${operator} privacy policy`,
    marketing: (operator) => `Email me news about available homes from ${operator} (optional)`,
    submit: 'Book the tour',
    sending: 'Sending…',
    sentTitle: 'Check your email',
    sentBody: (email, hours) =>
      `We sent a link to confirm the tour to ${email}. The time is held for you for ${hours} hours until you confirm.`,
    sentSpam: 'Not there? Check your spam or promotions folder.',
    notFoundTitle: 'This page is not available',
    notFoundBody: 'There is no tour booking page here. Check the link or contact the office.',
    loadError: 'We could not load the page. Check your connection and try again.',
    retry: 'Try again',
    errors: {
      TOUR_SLOT_TAKEN: 'That time was just taken. Please pick another.',
      TOUR_SLOT_UNAVAILABLE: 'That time is no longer offered. Please pick another.',
      TOUR_NOTICE_CHANGED: 'The privacy notice was updated. Please read it again and send.',
      TOUR_BOT_CHECK: 'The security check did not pass. Please try again.',
      RATE_LIMITED: 'Too many attempts. Please try again later.',
      TOUR_EXPIRED: 'This request expired. You can book a new tour.',
      TOUR_NOT_CANCELLABLE: 'This tour cannot be cancelled from the link. Please contact the office.',
      DEFAULT: 'Something went wrong. Please try again.',
    },
    required: 'Required',
    badEmail: 'This email does not look right',
    badPhone: 'Digits only',
    changeTime: 'Change time',
    manageTitle: 'Your tour',
    pendingBody: 'The tour is not confirmed yet. Confirm to keep the time.',
    confirm: 'Confirm the tour',
    confirming: 'Confirming…',
    confirmedTitle: 'Your tour is booked',
    confirmedBody: 'We emailed you the details. We look forward to seeing you.',
    cancel: 'Cancel the tour',
    cancelAsk: 'Cancel the tour?',
    cancelYes: 'Yes, cancel',
    cancelNo: 'No, keep it',
    cancelledTitle: 'Your tour is cancelled',
    cancelledBody: 'The time is free again. You can book a new one whenever you like.',
    expiredTitle: 'This request expired',
    expiredBody: 'The tour was not confirmed in time, so it was not kept.',
    pastTitle: 'This tour has passed',
    pastBody: 'For another tour, book a new time.',
    bookAgain: 'Book a new tour',
    linkInvalidTitle: 'This link is not valid',
    linkInvalidBody: 'The link may be cut off or old. Open it again from the email, or contact the office.',
    when: 'When',
    what: 'Home type',
  },
};

export function tourLangOf(raw: string | null | undefined): TourLang {
  return raw === 'en' ? 'en' : 'he';
}

/** "Thursday, 5 November" and "16:00" in the company's zone. */
export function dayLabel(iso: string, timeZone: string, s: TourStrings): string {
  return new Intl.DateTimeFormat(s.locale, { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(iso),
  );
}
export function timeLabel(iso: string, timeZone: string, s: TourStrings): string {
  return new Intl.DateTimeFormat(s.locale, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  );
}
